/**
 *
 * Table
 *
 */
import StyleComponentProps from 'core/utils/types/StyleComponentProps';
import classNames from 'classnames';
import React, { memo, ReactNode } from 'react';
import styled from 'styled-components/macro';
import {
  PaginationState,
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  getSortedRowModel,
} from '@tanstack/react-table';
import { Button } from '../Button';
// import { Link } from 'react-router-dom';
import { ReactComponent as LeftArrow } from 'assets/icons/LeftArrow.svg';
import { ReactComponent as RightArrow } from 'assets/icons/RightArrow.svg';
import { ReactComponent as Print } from 'assets/icons/Print.svg';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
// import { paginationOptions } from 'core/utils/constants/DateTime';
import sort from 'assets/images/sort.png';
import NoDataFound from '../NoDataFound';
import ReactToPrint from 'react-to-print';
import Modal from 'react-modal';
import { ReactComponent as Close } from 'assets/icons/Close.svg';
import { DropDown } from 'libs/DropDown';
import { formatString, reorderTable } from 'core/utils/constants';
import { useParams } from 'react-router-dom';
import { SetupLoader } from '../SetupComponents/SetupLoader';
import httpclient from 'libs/Axios';
import { useDispatch } from 'react-redux';
import { useAuthenticationSlice } from 'features/Authentication/slice';
import { error } from '../Notify';
import debounce from 'lodash/debounce';
import StatusColorIndicator from '../StatusColorIndicator';

export const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '30rem',
    // minHeight: '25rem',
    borderRadius: '1.5em',
    boxShadow: '0px 16px 32px 4px rgba(0, 0, 0, 0.15)',
    padding: '1.3em',
  },
  overlay: {
    background: 'rgba(0, 0, 0, 0.4)',
  },
};

interface Props extends StyleComponentProps {
  columns: any[];
  data: any[];
  onRowClick?: any;
  hoverTransparent?: boolean;
  pageIndex?: number;
  pageSize?: number;
  defaultColumns?: {
    minSize: number;
    maxSize: number;
  };
  setPagination?: React.Dispatch<React.SetStateAction<PaginationState>>;
  notWantPages?: boolean;
  isPrint?: boolean;
  previousYearValidationNotRequired?: boolean;
  totalEntries: number;
  columnsSelectionDispatch?: any;
  defaultColumnsForSelectection?: any;
  customData?: any;
  headerControlsLeft?: () => ReactNode;
}

export function fetchData(
  data: any[],
  options: {
    pageIndex: number;
    pageSize: number;
  },
) {
  return {
    rows: data?.slice(
      options.pageIndex * options.pageSize,
      (options.pageIndex + 1) * options.pageSize,
    ),
    pageCount: Math.ceil(data?.length / options.pageSize),
  };
}

const paginationOptions = [10, 20, 25, 50, 100, 200, 500, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 4500, 5000];

export const StaticTable = memo((props: Props) => {


  const constantColumns = props?.defaultColumnsForSelectection?.map(item => ({
    ...item,
    ...(!item?.id && { id: item?.accessorKey || item?.header })
  }));

  const headerRef: any = React.useRef(null);
  const tableExportRef = React.useRef(null);
  const authData = useAuth();
  const params = useParams();
  const dispatch = useDispatch();
  const { actions } = useAuthenticationSlice();
  // const dataQuery = fetchData(props?.data, fetchDataOptions);

  const [{ pageIndex, pageSize }, setPagination] = React.useState<PaginationState>({ pageIndex: 0, pageSize: paginationOptions.at(0) as number });
  const [sorting, setSorting] = React.useState<SortingState>([]);
  // const [tableData, setTableData] = React.useState(props.data);
  const [searchInput, setSearchInput] = React.useState('');
  const tableRef: any = React.useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [formLoading, setFormLoading] = React.useState(false);
  const [showModal, setshowModal] = React.useState({ display: false, ID: undefined });
  const [selectedColumns, setSelectedColumns] = React.useState<any>([]);
  const [selectedSavedColumns, setselectedSavedColumns] = React.useState<any>(authData?.COLUMNS?.find(item => item?.MODULE_NAME === params?.route) || {});
  const [debouncedSearchInput, setDebouncedSearchInput] = React.useState('');
  const [isSearching, setIsSearching] = React.useState(false);

  // const filteredData = React.useMemo(() => {
  //   if (!searchInput) return tableData;
  //   return tableData.filter(row =>
  //     Object.values(row).some(
  //       (value: any) =>
  //         value &&
  //         value?.toString()?.toLowerCase()?.includes(searchInput?.toLowerCase())
  //     )
  //   );
  // }, [searchInput, tableData]);

  const debounceSearch = React.useMemo(
    () =>
      debounce((value) => {
        setDebouncedSearchInput(value);
      }, 300),
    []
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debounceSearch(value);
  };


  const filterRow = (row, query) => {
    if (typeof row === 'object' && row !== null) {
      return Object.values(row)?.some((value) => filterRow(value, query));
    }
    return row && row.toString().toLowerCase().includes(query.toLowerCase());
  };

  // Filtered data for search
  const filteredData = React.useMemo(() => {
    if (!debouncedSearchInput || debouncedSearchInput.length < 3) {
      setIsSearching(false);
      return props.data;
    }
    setIsSearching(true);
    return props.data?.filter((row) => filterRow(row, debouncedSearchInput));
  }, [debouncedSearchInput, props.data]);


  React.useEffect(() => {
    const handleScroll = () => {
      if (!!headerRef) {
        const table = headerRef?.current?.closest('div[style*="overflow-y: auto"]');
        if (table) {
          const scrollTop = table?.scrollTop;
          headerRef.current.style.transform = `translateY(${scrollTop}px)`;
        }
      }
    };

    const container = headerRef?.current?.closest('div[style*="overflow-y: auto"]');
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  // React.useEffect(() => {
  //   setTableData(props.data);
  // }, [props.data]);

  const fetchDataOptions = {
    pageIndex: pageIndex,
    pageSize: pageSize,
  };

  // const defaultData = React.useMemo(() => [], []);
  // const dataQuery = fetchData(filteredData, fetchDataOptions);

  React.useEffect(() => {
    // Force table to recognize page change
    if (tableRef.current) {
      // Scroll to top when page changes
      const scrollContainer = tableRef.current.closest('div[style*="overflow-y: auto"]');
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      }
    }
  }, [pageIndex, pageSize]);

  // Fix 4: Ensure dataQuery updates properly
  const dataQuery = React.useMemo(() => {
    return fetchData(filteredData, fetchDataOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData, pageIndex, pageSize]); // Add dependencies

  // Fix 5: Reset page to 0 when filtered data changes
  React.useEffect(() => {
    setPagination(prev => ({
      ...prev,
      pageIndex: 0
    }));
  }, [filteredData.length]);

  const pagination = React.useMemo(
    () => ({
      pageIndex: pageIndex,
      pageSize: pageSize,
    }),
    [pageIndex, pageSize],
  );

  const table = useReactTable({
    data: dataQuery.rows ?? [],
    pageCount: dataQuery.pageCount ?? -1,
    columns: props.columns?.filter(item => {
      if (!props?.previousYearValidationNotRequired) {
        if (
          authData?.SELECTED_ACADEMIC_YEAR?.ACADEMIC_YEAR_STATUS !==
          'PREVIOUS' ||
          item?.id !== 'ACTION_HEADER'
        ) {
          return item;
        }
      } else {
        return item;
      }
    }),
    defaultColumn:
      props.defaultColumns &&
      (props.defaultColumns as Partial<ColumnDef<any, unknown>> | undefined),
    state: {
      pagination,
      sorting
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    // getPaginationRowModel: getPaginationRowModel(), // If only doing manual pagination, you don't need this
    onSortingChange: setSorting,
    // debugTable: true,
    getRowId: (row, index) => {
      // Generate truly unique row ID across all pages
      const idField = Object.keys(row).find(key =>
        key.endsWith('_ID') || key === 'ID'
      );

      if (idField && row[idField] != null) {
        // Use the actual ID from data (preferred)
        return `row-${row[idField]}`;
      }

      // CRITICAL: Use global index that includes page number
      const globalIndex = pageIndex * pageSize + index;
      return `page-${pageIndex}-row-${globalIndex}`;
    }
  });

  const fetchListData = async () => {
    try {
      setFormLoading(formLoading => true);
      const response = await httpclient().get(`/master/tableColumnDisplay/list?institute_id=${authData?.INSTITUTE_ID}&branch_id=${authData?.BRANCH_ID}`)
      const data = response?.data?.data?.TableColumnDisplayList?.find(item => item?.MODULE_NAME === params?.route) || {};
      if (data?.DISPLAY_COLUMNS?.length) {
        setselectedSavedColumns(data);
        dispatch(actions.updateAuthData({
          COLUMNS: response?.data?.data?.TableColumnDisplayList
        }))
      }
      setFormLoading(formLoading => false);
    } catch (error) {
      console.error(error);
      setFormLoading(formLoading => false);
    }
  }

  const handleSelectedColumnSelection = async () => {
    try {
      if (selectedColumns?.length) {
        setFormLoading(formLoading => true);
        const response = await httpclient().post(`/master/tableColumnDisplay/upsert`, [
          {
            TABLE_COLUMN_DISPLAY_ID: selectedSavedColumns?.TABLE_COLUMN_DISPLAY_ID,
            INSTITUTE_ID: authData?.INSTITUTE_ID,
            BRANCH_ID: authData?.BRANCH_ID,
            MODULE_NAME: params?.route,
            DISPLAY_COLUMNS: selectedColumns
          }
        ]);
        if (!!response) {
          fetchListData();
        } else {
          setFormLoading(formLoading => false);
        }
      } else {
        error("Please Select One Column at least!", true);
      }
    } catch (err) {
      setFormLoading(formLoading => false);
      console.log(err)
    }
  }



  const downloadCSV = (csv, filename) => {
    const csvFile = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const downloadLink = document.createElement('a');
    downloadLink.download = filename;
    downloadLink.href = window.URL.createObjectURL(csvFile);
    downloadLink.style.display = 'none';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const exportTableToCSV = (filename) => {
    const XLSX = require("xlsx-js-style");
    let ws_data: any[][] = [];

    if (params?.route === "examStudentResult") {
      let headersRow = ["Student Name", "Class", "Roll No"];
      props?.customData?.ExamDetails?.Subjects?.forEach((item: any) => {
        headersRow.push(`${item?.Subject?.SUBJECT_NAME} (${item?.TOTAL_MARKS})`);
      });
      let totalMaxMarks = props?.customData?.ExamDetails?.Subjects?.reduce((sum: number, current: any) => sum + +(current?.TOTAL_MARKS || 0), 0);
      headersRow.push(`Total Marks (${totalMaxMarks})`);
      
      ws_data.push(headersRow);

      props?.customData?.StudentResults?.forEach((item: any) => {
        let rowArray: string[] = [];
        rowArray.push(`${item?.FIRST_NAME || ""} ${item?.LAST_NAME || ""}`);
        rowArray.push(`${props?.customData?.ExamDetails?.Class?.NAME || ""}`);
        rowArray.push(`${item?.StudentClass?.ROLL_NUMBER || ""}`);

        props?.customData?.ExamDetails?.Subjects?.forEach((subject: any) => {
          let result = item?.Results?.find((r: any) => r?.EXAM_SUBJECT_ID === subject?.EXAM_SUBJECT_ID);
          rowArray.push(`${result?.MARKS_OBTAINED !== undefined && result?.MARKS_OBTAINED !== null ? result?.MARKS_OBTAINED : "-"}`);
        });

        rowArray.push(`${item?.TotalObtainedMarks !== undefined && item?.TotalObtainedMarks !== null ? item?.TotalObtainedMarks : "-"}`);
        ws_data.push(rowArray);
      });

      const ws = XLSX.utils.aoa_to_sheet(ws_data);

      for (let R = 0; R < ws_data.length; ++R) {
        if (R === 0) {
          for (let C = 0; C < ws_data[R].length; ++C) {
            const cellRef = XLSX.utils.encode_cell({ c: C, r: R });
            if (!ws[cellRef]) continue;
            ws[cellRef].s = { font: { bold: true } };
          }
        }
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Export");
      XLSX.writeFile(wb, filename);

    } else if (params?.route === "accountTransaction") {
      const table: any = tableExportRef.current;
      const rows = table.querySelectorAll('tr');

      const isTrue = props?.columns?.find(item => item?.id === "ACTION_HEADER");
      
      rows.forEach((row, rowIdx) => {
        const cols = row.querySelectorAll('td, th');
        const rowData: any[] = [];
        cols?.forEach((col, index) => {
          const hiddenSpan = col.querySelector('#hiddenLeafExcel') || "";
          if (isTrue) {
            if ((cols?.length - 1) !== index) {
              if (hiddenSpan) {
                rowData.push(formatString((hiddenSpan as any).innerText))
              } else {
                rowData.push(formatString((col as any).innerText))
              }
            }
          } else {
            if (hiddenSpan) {
              rowData.push(formatString((hiddenSpan as any).innerText))
            } else {
              rowData.push(formatString((col as any).innerText))
            }
          }
        });
        // Add Student Status column
        if (rowIdx === 0) {
          rowData.push("Student Status");
        } else {
          if (row.classList.contains('bg-yellow-300')) rowData.push("Inactive");
          else if (row.classList.contains('line-through')) rowData.push("Deleted");
          else rowData.push("Active");
        }
        ws_data.push(rowData);
      });

      const ws = XLSX.utils.aoa_to_sheet(ws_data);

      rows.forEach((row, R) => {
        let color = "";
        if (R === 0) {
          for (let C = 0; C < ws_data[R].length; ++C) {
            const cellRef = XLSX.utils.encode_cell({ c: C, r: R });
            if (!ws[cellRef]) continue;
            ws[cellRef].s = { font: { bold: true } };
          }
        } else {
          let hasColor = false;
          let isStrike = false;
          if (row.classList.contains('bg-yellow-300')) { color = "FFFDE047"; hasColor = true; }
          if (row.classList.contains('line-through')) isStrike = true;

          if (hasColor || isStrike) {
            for (let C = 0; C < ws_data[R].length; ++C) {
              const cellRef = XLSX.utils.encode_cell({ c: C, r: R });
              if (!ws[cellRef]) continue;
              
              if (!ws[cellRef].s) ws[cellRef].s = {};
              if (hasColor) ws[cellRef].s.fill = { fgColor: { rgb: color } };
              if (isStrike) {
                  if (!ws[cellRef].s.font) ws[cellRef].s.font = {};
                  ws[cellRef].s.font.strike = true;
              }
            }
          }
        }
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Export");
      XLSX.writeFile(wb, filename.replace('.csv', '.xlsx'));
    } else {
      const table: any = tableExportRef.current;
      const rows = table.querySelectorAll('tr');
      const csv: any = [];
      const isTrue = props?.columns?.find(item => item?.id === "ACTION_HEADER");
      
      rows.forEach(row => {
        const cols = row.querySelectorAll('td, th');
        const rowData: any = [];
        cols?.forEach((col, index) => {
          const hiddenSpan = col.querySelector('#hiddenLeafExcel') || "";
          if (isTrue) {
            if ((cols?.length - 1) !== index) {
              if (hiddenSpan) {
                rowData.push(formatString((hiddenSpan as any).innerText))
              } else {
                rowData.push(formatString((col as any).innerText))
              }
            }
          } else {
            if (hiddenSpan) {
              rowData.push(formatString((hiddenSpan as any).innerText))
            } else {
              rowData.push(formatString((col as any).innerText))
            }
          }
        });
        csv.push(rowData.join(','));
      });
      downloadCSV(csv.join('\n'), filename);
    }
  };

  return (
    <>
      <Div ref={tableRef} className={`min-w-full p-4 space-y-2 ${props.className}`}>
        <Modal
          isOpen={showModal.display}
          contentLabel="Example Modal"
          style={customStyles}
        >
          <div className="flex justify-between">
            <h1 className="font-bold text-xl">Columns</h1>
            <Close
              className="w-6 h-6 cursor-pointer"
              onClick={() => {
                setSelectedColumns([]);
                setshowModal({ display: false, ID: undefined })
              }}
            />
          </div>
          <div className="flex flex-col justify-between items-between h-[50vh] gap-4 py-2">
            <div>
              {!!formLoading ? <SetupLoader height='h-10' /> : <DropDown
                notCloseMenuOnSelect={true}
                menuHeight='100px'
                minWidth={200}
                height='auto'
                isMulti
                getOptionValue={(option: any) => option?.id?.toUpperCase()}
                getOptionLabel={(option: any) => typeof (option?.header) === "string" ? (option?.header || option?.id) : option?.id}
                options={constantColumns /* table.getHeaderGroups()?.[0]?.headers */}
                placeholder="Select Columns"
                onChange={(column: any) => {
                  setSelectedColumns(selectedColumns => column?.map(({ id, header, accessorKey }) => (id || accessorKey || header)))
                }}
                defaultValue={reorderTable(constantColumns, selectedSavedColumns?.DISPLAY_COLUMNS)?.filter(({ id, header, accessorKey }) => selectedSavedColumns?.DISPLAY_COLUMNS?.find(item => (item === id || item === accessorKey || item === header)))}
                name="COLUMNS"
              />}
            </div>
            <div className="flex justify-center">
              <div className="flex gap-2">
                <Button
                  type="ghost"
                  label="Cancel"
                  onClick={() => {
                    setSelectedColumns([]);
                    setshowModal({ display: false, ID: undefined })
                  }}
                />
                {/* <Button
                  type="outlined"
                  label="Apply"
                  onClick={() => {
                    props?.columnsSelectionDispatch(selectedColumns);
                    setshowModal({ display: false, ID: undefined });
                  }}
                /> */}
                {/* formLoading ? <SetupLoader height='h-10' /> : */ <Button
                  type="primary"
                  label="Save & Apply"
                  onClick={() => {
                    handleSelectedColumnSelection()
                    // setshowModal({ display: false, ID: undefined });
                  }}
                />}
              </div>
            </div>
          </div>
        </Modal>
        <div className="print:hidden w-full flex justify-between items-center gap-4 mb-2">
          <div className="flex-1 flex justify-start items-center">
             <div className="flex items-center gap-3">
               {!!props?.headerControlsLeft && props?.headerControlsLeft()}
             </div>
          </div>
          <div className="flex justify-end items-center gap-4">
            <select className='shadow p-2 rounded-lg' onChange={(e) => {
              setPagination({
                pageIndex: 0, // pageIndex
                pageSize: +e.target.value
              })
            }}>
              {paginationOptions?.map((item, i) => <option key={"oqweioqhehjsehwje" + i} value={item}>
                {item}
              </option>)}
            </select>
            {props?.isPrint && <ReactToPrint
              trigger={() => <div className='cursor-pointer bg-neutral-light p-2 shadow rounded-full'><Print className='max-h-20' /></div>
              }
              content={() => tableRef.current}
            />}
            {!!props?.columnsSelectionDispatch && <button onClick={() => setshowModal({ display: true, ID: undefined })} className='border-[1px] border-solid border-primary-main bg-neutral-light py-2 px-4 rounded-lg text-primary-main font-semibold'>
              Columns
            </button>}
            <button onClick={() => exportTableToCSV(`${params?.route || "my_table"}.csv`)} className='bg-primary-main py-2 px-4 rounded-lg text-neutral-light font-semibold'>
              Export
            </button>
          </div>
        </div>
        <ScrollableDiv
          // ref={tableContainerRef}
          style={{
            height: '62vh', // Adjust table height to fit your layout
            overflowY: 'auto',
          }}
        >
          {!!filteredData?.length && filteredData?.length > 0 ? (

            <table ref={tableExportRef} className="min-w-full"/*  style={{
            height: '40vh', // Adjust table height to fit your layout
            overflowY: 'auto',
            border: "10px solid black"
          }} */>
              <thead ref={headerRef}>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => {
                      return (
                        <th
                          key={header.id}
                          style={{
                            maxWidth: header.column.columnDef.maxSize,
                          }}
                          // colSpan={header.colSpan}
                          className={`!max-w-[40vw] min-w-[7vw] ${header.column.columnDef['className']}`}
                        >
                          {header.isPlaceholder ? null : (
                            <div onClick={header.column.getToggleSortingHandler()} className={`cursor-pointer ${!index && '!pl-6'} !min-w-max`}>
                              {header.column.columnDef.header
                                ? flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )
                                : header.column.columnDef.header}
                              {{ asc: "🠁", desc: "🠃" }[header.column.getIsSorted() as string] ?? null}
                            </div>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row, rowIndex) => {
                  // Add rowIndex to ensure uniqueness
                  const uniqueKey = `${pageIndex}-${row.id}-${rowIndex}`;

                  const status = row.original?.DisplayStudentClassStatus?.toUpperCase();
                  const trClass = classNames(
                    'cursor-pointer',
                    !props.hoverTransparent && 'hover:bg-neutral-30',
                    {
                      'bg-yellow-300': status === 'INACTIVE',
                      'line-through text-gray-400': status === 'DELETED'
                    }
                  );

                  return (
                    <tr
                      className={trClass}
                      key={uniqueKey} // Use compound key
                      onClick={() => {
                        if (!!props?.onRowClick) {
                          props?.onRowClick(row)
                        }
                      }}
                    >
                      {row.getVisibleCells().map((cell, cellIndex) => {
                        // CRITICAL: Add unique key for each cell too
                        const cellKey = `${uniqueKey}-cell-${cell.column.id}-${cellIndex}`;

                        return (
                          <td
                            key={cellKey} // Unique cell key
                            className={`!max-w-[40vw] min-w-[7vw] ${cell.column.columnDef['className']}`}
                          >
                            <div className={`${!cellIndex && '!pl-6'} !min-w-max text-left`}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>

          ) : (<div className='w-full h-full flex justify-center items-center'><NoDataFound /></div>)}
        </ScrollableDiv>
      </Div>
      {(!props?.notWantPages && !!table.getRowModel().rows?.length) && (
        <div className="sticky w-full flex justify-between p-[0.8vw]">
          <div className="flex gap-1 w-fit whitespace-nowrap">
            <span>
              Showing{" "}
              <span className="font-semibold text-gray-900 dark">
                {table.getState().pagination.pageIndex *
                  table.getState().pagination.pageSize +
                  1}{" "}
              </span>
              to{" "}
              <span className="font-semibold text-gray-900 dark">
                {Math.min(
                  table.getState().pagination.pageIndex *
                  table.getState().pagination.pageSize +
                  table.getState().pagination.pageSize,
                  props?.data?.length,
                )}{" "}
              </span>
              of{" "}
              <span className="font-semibold text-gray-900 dark">
                {props?.data?.length}{" "}
              </span>{' '}
              entries out of{" "}
              <span className="font-semibold text-gray-900 dark">
                {props?.totalEntries || props?.data?.length || 0}{" "}
              </span>
              entries
            </span>
          </div>
          <div className="w-full flex justify-end items-center gap-2">
            <Button
              type="outlined"
              icon={<LeftArrow className="w-4 h-4" />}
              width="w-8 h-8"
              labelClasses="text-xs"
              padding="px-2"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            />
            {dataQuery.pageCount < 9
              ? Array.from(Array(dataQuery.pageCount).keys()).map(
                (item, index) => (
                  <Button
                    key={index}
                    type={
                      table.getState().pagination.pageIndex === index
                        ? 'primary'
                        : 'outlined'
                    }
                    label={(item + 1).toString()}
                    padding="px-2"
                    width="w-8 h-8"
                    labelClasses="text-xs notranslate"
                    onClick={() => table.setPageIndex(index)}
                  />
                ),
              )
              : table.getState().pagination.pageIndex > 6 &&
                table.getState().pagination.pageIndex < dataQuery.pageCount - 6
                ? [
                  1,
                  '...',
                  table.getState().pagination.pageIndex - 1,
                  table.getState().pagination.pageIndex,
                  table.getState().pagination.pageIndex + 1,
                  table.getState().pagination.pageIndex + 2,
                  table.getState().pagination.pageIndex + 3,
                  '...',
                  dataQuery.pageCount,
                ].map((item: any, index) => (
                  <Button
                    key={index}
                    type={
                      table.getState().pagination.pageIndex + 1 === item
                        ? 'primary'
                        : 'outlined'
                    }
                    label={item.toString()}
                    width="w-8 h-8"
                    labelClasses="text-xs notranslate"
                    padding="px-2"
                    onClick={() => {
                      if (index === 0) {
                        table.setPageIndex(0);
                      } else if (index === 8) {
                        table.setPageIndex(dataQuery.pageCount - 1);
                      } else {
                        table.setPageIndex(item - 1);
                      }
                    }}
                    disabled={item === '...' ? true : false}
                  />
                ))
                : dataQuery.pageCount -
                  1 -
                  table.getState().pagination.pageIndex >
                  table.getState().pagination.pageIndex
                  ? [1, 2, 3, 4, 5, 6, 7, '...', dataQuery.pageCount].map(
                    (item, index) => (
                      <Button
                        key={index}
                        type={
                          table.getState().pagination.pageIndex + 1 === item
                            ? 'primary'
                            : 'outlined'
                        }
                        label={item.toString()}
                        width="w-8 h-8"
                        padding="px-2"
                        labelClasses="text-xs notranslate"
                        onClick={() => {
                          if (index === 8) {
                            table.setPageIndex(dataQuery.pageCount - 1);
                          } else {
                            table.setPageIndex(index);
                          }
                        }}
                        disabled={item === '...' ? true : false}
                      />
                    ),
                  )
                  : [
                    1,
                    '...',
                    dataQuery.pageCount - 6,
                    dataQuery.pageCount - 5,
                    dataQuery.pageCount - 4,
                    dataQuery.pageCount - 3,
                    dataQuery.pageCount - 2,
                    dataQuery.pageCount - 1,
                    dataQuery.pageCount,
                  ].map((item: any, index) => (
                    <Button
                      key={index}
                      type={
                        table.getState().pagination.pageIndex + 1 === item
                          ? 'primary'
                          : 'outlined'
                      }
                      label={item.toString()}
                      width="w-8 h-8"
                      padding="px-2"
                      labelClasses="text-xs notranslate"
                      onClick={() => table.setPageIndex(item - 1)}
                      disabled={item === '...' ? true : false}
                    />
                  ))}
            <Button
              type="outlined"
              icon={<RightArrow className="w-4 h-4" />}
              width="w-8 h-8"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              padding="p-2"
            />
          </div>
        </div>
      )}
    </>
  );
});

const Div = styled.div`
  max-width: 100% !important;
  overflow: auto !important;

  table {
    box-shadow: 0px 8px 24px rgba(69, 69, 80, 0.1);
    background-color: white;
    border-collapse: collapse;
    border-spacing: 0;
    table-layout: auto;
    width: 100%;
    border-radius: 16px;
    overflow: clip;
  }
  thead {
    border-radius: 16px;
    background-color: rgba(237, 234, 254, 1);
  }
  th {
    padding: 0.9rem;
    color: rgba(0, 0, 0, 1);
    font-weight: 600;
    text-align: left;
    border-bottom: 1px solid #e0e3e7;
  }

  td {
    padding: 0.9rem;
    border-bottom: 1px solid #e0e3e7;
  }

  .space {
    border-bottom: none;
    width: 0.2vw;
    /* width: 0.5vw; */
  }
`;

const ScrollableDiv = styled.div`
  overflow: auto;

  /* Default scrollbar size */
  &::-webkit-scrollbar {
    width: 2px;
    height: 2px;
    transition: width 0.3s ease, height 0.3s ease;
  }

  /* Track */
  &::-webkit-scrollbar-track {
    box-shadow: inset 0 0 5px grey;
    border-radius: 10px;
  }

  /* Handle */
  &::-webkit-scrollbar-thumb {
    background: #1100ab;
    border-radius: 10px;
    transition: background 0.3s ease;
  }

  /* Handle on hover */
  &::-webkit-scrollbar-thumb:hover {
    background: gray;
  }

  /* Enlarge scrollbar when hovering over the container */
  &:hover::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
`;